import { useEffect, useId, useRef, useState } from "react"
import Icon from "../../components/Icon"

// Shared search dropdown for the billing console: a search box that opens its
// full list, narrows it as you type, and closes once a row is picked. The
// caller owns the selection and decides how a row looks, so the same control
// works for items/categories and for customers.
//
// `options` are `{ key, label, searchText }` plus whatever the caller needs.
function SearchDropdown({
  options = [],
  selectedKey = "",
  onSelect,
  onClear,
  placeholder = "Search…",
  emptyText = "Nothing matches your search.",
  renderOption,
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  // While typing, the box shows the query; otherwise it shows the picked row.
  // Deriving it this way keeps the box correct when the caller clears the
  // selection from somewhere else.
  const [typing, setTyping] = useState(false)
  const rootRef = useRef(null)
  const inputRef = useRef(null)
  const listId = useId()

  useEffect(() => {
    if (!open) return
    const onClickOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false)
    }
    const onEscape = (event) => {
      if (event.key === "Escape") setOpen(false)
    }
    document.addEventListener("mousedown", onClickOutside)
    document.addEventListener("keydown", onEscape)
    return () => {
      document.removeEventListener("mousedown", onClickOutside)
      document.removeEventListener("keydown", onEscape)
    }
  }, [open])

  const selected = options.find((option) => String(option.key) === String(selectedKey)) || null
  const displayValue = typing ? query : selected?.label || ""
  const trimmedQuery = typing ? query.trim().toLowerCase() : ""
  const visible = trimmedQuery
    ? options.filter((option) =>
        String(option.searchText || option.label || "").toLowerCase().includes(trimmedQuery)
      )
    : options

  const pick = (option) => {
    setTyping(false)
    setQuery("")
    setOpen(false)
    if (onSelect) onSelect(option)
  }

  const clear = () => {
    setTyping(false)
    setQuery("")
    setOpen(true)
    if (onClear) onClear()
    inputRef.current?.focus()
  }

  return (
    <div ref={rootRef} className="relative">
      <div
        className={`flex items-center gap-2 rounded-xl border bg-white px-3 py-2 transition-colors ${
          open ? "border-teal-500 ring-2 ring-teal-100" : "border-slate-300 hover:border-slate-400"
        }`}
      >
        <Icon name="search" size="0.75rem" className="shrink-0 text-slate-400" />
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          value={displayValue}
          onChange={(event) => {
            setTyping(true)
            setQuery(event.target.value)
            setOpen(true)
          }}
          onFocus={(event) => {
            setOpen(true)
            event.target.select()
          }}
          placeholder={placeholder}
          className="min-w-0 flex-1 bg-transparent text-sm font-semibold text-slate-800 placeholder:font-normal placeholder:text-slate-400 focus:outline-none"
        />
        {displayValue && (
          <button
            type="button"
            onClick={clear}
            aria-label="Clear search"
            className="shrink-0 rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
          >
            <Icon name="x-circle-fill" size="0.75rem" />
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            setOpen((value) => !value)
            inputRef.current?.focus()
          }}
          aria-label={open ? "Hide list" : "Show list"}
          className="shrink-0 rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
        >
          <Icon name={open ? "chevron-up" : "chevron-down"} size="0.7rem" />
        </button>
      </div>

      {open && (
        <div
          id={listId}
          role="listbox"
          className="absolute z-20 mt-1.5 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg"
        >
          {visible.length === 0 ? (
            <p className="px-3 py-3 text-xs text-slate-400">{emptyText}</p>
          ) : (
            <div className="max-h-60 overflow-y-auto py-1">
              {visible.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  role="option"
                  aria-selected={String(option.key) === String(selectedKey)}
                  onClick={() => pick(option)}
                  className={`flex w-full items-center px-3 py-2 text-left text-sm transition-colors hover:bg-teal-50 ${
                    String(option.key) === String(selectedKey) ? "bg-teal-50" : ""
                  }`}
                >
                  {renderOption(option)}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default SearchDropdown
